import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { WatchQueryOptions } from 'apollo-angular/types';
import { ApolloQueryResult } from 'apollo-client';
import gql from 'graphql-tag';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { MasterDataEnum } from '../enum/master-data.enum';
import { GraphqlQueryObject, OrderByEnum, OrderDirectionEnum } from '../gql/common.gql';
import { CrossDockSupplierContent } from '../models/crossdock-supplier.model';
import { WarehouseListContent } from '../models/warehouse.model';

@Injectable({
  providedIn: 'root'
})
export class MasterService {
  public watchQuery<T = any, V = any>(options: WatchQueryOptions<V>): any {
    return this.apollo
      .watchQuery<T, V>(options)
      .valueChanges.pipe(tap(value => this.logger.debug('watchQuery', value)));
  }

  public getMasterDataByNames<T = any, V = any>(
    names: Array<string | GraphqlQueryObject>
  ): Observable<ApolloQueryResult<T>> {
    return names.length ? this.watchQuery<T, V>({ query: this.getGraphQuery(names) }) : of(null);
  }

  public clearStore(): void {
    this.apollo.client.clearStore().then(r => r);
  }

  public getWarehouseList(): Observable<WarehouseListContent[]> {
    const warehouseQuery = new GraphqlQueryObject();
    warehouseQuery.name = MasterDataEnum.WAREHOUSE;
    warehouseQuery.fields = [
      'id',
      'code',
      'nameTh',
      'wmsCode',
      'defaultFirstLot',
      'operator',
      'orderFlow',
      'crossDockSupplier',
      'defaultStorageType',
      'allStorageTypes',
      'splitStorageTypes'
    ];
    warehouseQuery.sort = {
      orderBy: OrderByEnum.WMS_CODE,
      orderDirection: OrderDirectionEnum.ASC
    };

    return this.getMasterDataByNames([warehouseQuery]).pipe(
      map(result =>
        (result?.data?.warehouses || []).map(warehouse => ({
          ...warehouse,
          warehouseNameDisplay: `${warehouse.wmsCode}-${warehouse.nameTh}`
        }))
      )
    );
  }

  public getCrossDockSupplierList(): Observable<CrossDockSupplierContent[]> {
    const crossDockSupplierQuery = new GraphqlQueryObject();
    crossDockSupplierQuery.name = MasterDataEnum.CROSS_DOCK_SUPPLIERS;
    crossDockSupplierQuery.defaultFields = ['id', 'name', 'code'];

    return this.getMasterDataByNames([crossDockSupplierQuery]).pipe(
      map(result => result?.data?.crossDockSuppliers || [])
    );
  }

  constructor(
    private readonly apollo: Apollo,
    protected readonly logger: NGXLogger
  ) {}

  /**
   *
   * Sample usage
   * const dayQuery = new GraphqlQueryObject();
   * dayQuery.name = 'days';
   * dayQuery.sort = { orderBy: 'ID'} as GraphqlQuerySortOptions;
   * dayQuery.fields = dayQuery.defaultFields;
   *
   * const countryQuery = new GraphqlQueryObject();
   * countryQuery.name = 'countries';
   * countryQuery.fields = ['id', 'vatPct', 'defaultCurrency'];
   *
   * const stateQuery = new GraphqlQueryObject();
   * stateQuery.name = 'states';
   * stateQuery.sort = { orderBy: 'NAME_EN'} as GraphqlQuerySortOptions;
   * stateQuery.fields = [
   * 'code',
   * 'nameTh',
   * 'nameEn',
   * `region {
   *      nameTh
   *      code
   *    }`
   * ];
   */
  public getGraphQuery(names: Array<string | GraphqlQueryObject>) {
    const nodes = names.map(name => {
      if (typeof name === 'string') {
        return `${name} {
          id
          nameTh
          nameEn
          code
        }`;
      } else if (name instanceof GraphqlQueryObject) {
        return this.getGraphqlQueryObject(name);
      }
    });
    const nodesString = nodes.join('\n');

    // query {
    //   days(sort: { orderBy: ID }) {
    //     id
    //     nameTh
    //     nameEn
    //     code
    //   }
    // }

    // query {
    //   days(sort: { orderBy: ID }, type: '') {
    //     id
    //     nameTh
    //     nameEn
    //     code,
    //     type
    //   }
    // }

    return gql`
      query {
        ${nodesString}
      }
    `;
  }

  public getGraphqlQueryObject(name: GraphqlQueryObject) {
    const queryObject = name;
    const sort = queryObject.sort
      ? `sort: { orderBy: ${queryObject.sort.orderBy} ${
          queryObject.sort.orderDirection ? ', orderDirection: ' + queryObject.sort.orderDirection : ''
        }}`
      : null;
    const rawType = queryObject.type instanceof Array ? JSON.stringify(queryObject.type) : `"${queryObject.type}"`;
    const type = queryObject.type ? `type: ${rawType}` : null;
    const rawLocationType =
      queryObject.type instanceof Array ? JSON.stringify(queryObject.locationType) : `"${queryObject.locationType}"`;
    const locationType = queryObject.locationType ? `locationType: ${rawLocationType}` : null;
    const active = queryObject.active !== undefined ? `active: ${queryObject.active}` : null;
    const allowManualAdjustment =
      queryObject.allowManualAdjustment !== undefined
        ? `allowManualAdjustment: ${queryObject.allowManualAdjustment}`
        : null;
    const params = [sort, type, locationType, active, allowManualAdjustment].filter(Boolean).join(',');
    const queryParams = params ? `(${params})` : '';
    return `${queryObject.name}${queryParams} {
      ${(queryObject.fields ? queryObject.fields : queryObject.defaultFields).join('\n')}
    }`;
  }
}
